/**
 * CAROUSEL MODULE
 * Handles hero slider and product carousel functionality
 * Features: Auto-play, manual navigation, touch/swipe support, keyboard navigation
 */

(function() {
  'use strict';

  window.CarouselModule = {
    carousels: [],

    /**
     * Initialize carousel
     * @param {HTMLElement} element - Carousel container
     * @param {Object} options - Configuration options
     */
    init: function(element, options = {}) {
      if (!element) return;

      const carousel = {
        element: element,
        items: element.querySelectorAll('[data-carousel-item]'),
        dots: element.querySelectorAll('[data-carousel-dot]'),
        prevBtn: element.querySelector('[data-carousel-prev]'),
        nextBtn: element.querySelector('[data-carousel-next]'),
        currentIndex: 0,
        autoplayInterval: null,
        autoplayDelay: options.autoplayDelay || 5000,
        isAutoplay: options.autoplay !== false,
        isDragging: false,
        startX: 0,
        currentX: 0,
      };

      this.carousels.push(carousel);
      this.setupEventListeners(carousel);
      this.goToSlide(carousel, 0);

      if (carousel.isAutoplay) {
        this.startAutoplay(carousel);
      }

      // Resume autoplay on hover end
      element.addEventListener('mouseenter', () => this.stopAutoplay(carousel));
      element.addEventListener('mouseleave', () => {
        if (carousel.isAutoplay) this.startAutoplay(carousel);
      });
    },

    /**
     * Setup event listeners
     */
    setupEventListeners: function(carousel) {
      // Navigation buttons
      if (carousel.prevBtn) {
        carousel.prevBtn.addEventListener('click', () => this.prevSlide(carousel));
      }
      if (carousel.nextBtn) {
        carousel.nextBtn.addEventListener('click', () => this.nextSlide(carousel));
      }

      // Dot navigation
      carousel.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(carousel, index));
      });

      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.prevSlide(carousel);
        if (e.key === 'ArrowRight') this.nextSlide(carousel);
      });

      // Touch/Swipe support
      carousel.element.addEventListener('touchstart', (e) => this.handleTouchStart(carousel, e), false);
      carousel.element.addEventListener('touchmove', (e) => this.handleTouchMove(carousel, e), false);
      carousel.element.addEventListener('touchend', (e) => this.handleTouchEnd(carousel, e), false);
    },

    /**
     * Handle touch start
     */
    handleTouchStart: function(carousel, e) {
      carousel.isDragging = true;
      carousel.startX = e.touches[0].clientX;
    },

    /**
     * Handle touch move
     */
    handleTouchMove: function(carousel, e) {
      if (!carousel.isDragging) return;
      carousel.currentX = e.touches[0].clientX;
    },

    /**
     * Handle touch end
     */
    handleTouchEnd: function(carousel, e) {
      if (!carousel.isDragging) return;
      carousel.isDragging = false;

      const diff = carousel.startX - carousel.currentX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.nextSlide(carousel);
        } else {
          this.prevSlide(carousel);
        }
      }
    },

    /**
     * Go to specific slide
     */
    goToSlide: function(carousel, index) {
      const totalSlides = carousel.items.length;
      if (totalSlides === 0) return;

      carousel.currentIndex = (index + totalSlides) % totalSlides;

      // Update items visibility
      carousel.items.forEach((item, i) => {
        item.style.display = i === carousel.currentIndex ? 'block' : 'none';
        item.setAttribute('aria-hidden', i !== carousel.currentIndex);
      });

      // Update dots
      carousel.dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === carousel.currentIndex);
        dot.setAttribute('aria-current', i === carousel.currentIndex ? 'true' : 'false');
      });
    },

    /**
     * Next slide
     */
    nextSlide: function(carousel) {
      this.stopAutoplay(carousel);
      this.goToSlide(carousel, carousel.currentIndex + 1);
      if (carousel.isAutoplay) this.startAutoplay(carousel);
    },

    /**
     * Previous slide
     */
    prevSlide: function(carousel) {
      this.stopAutoplay(carousel);
      this.goToSlide(carousel, carousel.currentIndex - 1);
      if (carousel.isAutoplay) this.startAutoplay(carousel);
    },

    /**
     * Start autoplay
     */
    startAutoplay: function(carousel) {
      if (carousel.autoplayInterval) return;

      carousel.autoplayInterval = setInterval(() => {
        this.goToSlide(carousel, carousel.currentIndex + 1);
      }, carousel.autoplayDelay);
    },

    /**
     * Stop autoplay
     */
    stopAutoplay: function(carousel) {
      if (carousel.autoplayInterval) {
        clearInterval(carousel.autoplayInterval);
        carousel.autoplayInterval = null;
      }
    },
  };

  // Auto-initialize carousels with data-carousel attribute
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-carousel]').forEach(element => {
      const autoplayDelay = element.dataset.carouselDelay || 5000;
      const autoplay = element.dataset.carouselAutoplay !== 'false';
      window.CarouselModule.init(element, {
        autoplayDelay: parseInt(autoplayDelay),
        autoplay: autoplay,
      });
    });
  });
})();
